import { createContext, useContext, useMemo } from "react";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { CommandJob, Job, Step } from "~/components/build/Show/lib/types";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useJobs } from "~/components/build/Show/lib/useJobs";

type CurrentStepSelectionContextType = {
  currentStep: Step | null;
  currentStepUuid: Step["uuid"] | null;
  currentJob: Job | null;
  currentJobUuid: Job["id"] | null;
  currentLatestJob: Job | null;
  currentLatestJobUuid: Job["id"] | null;
};

const CurrentStepSelectionContext = createContext<CurrentStepSelectionContextType>({
  currentStep: null,
  currentStepUuid: null,
  currentJob: null,
  currentJobUuid: null,
  currentLatestJob: null,
  currentLatestJobUuid: null,
});

export function CurrentStepSelectionContextProvider(props: React.PropsWithChildren) {
  const { stepUuid, jobUuid } = useQueryParams();
  const { stepsMap } = useSteps();
  const jobs = useJobs();

  // Get the job based on the job UUID.
  const currentJob = useMemo(() => {
    return jobs.get(jobUuid || "");
  }, [jobUuid, jobs]);

  // Get the step based on the step UUID or the job's step UUID.
  const currentStep = useMemo(() => {
    return stepsMap.get(currentJob?.stepUuid || stepUuid || "");
  }, [currentJob?.stepUuid, stepUuid, stepsMap]);

  // Find the latest retry for the job (when applicable).
  const currentLatestJob = useMemo(() => {
    if (currentJob?.type === "script") {
      return findLatestRetry(currentJob, jobs);
    }

    if (stepUuid) {
      const latestJobId = stepsMap.get(stepUuid)?.statistics?.latestJobId;
      if (latestJobId) {
        return jobs.get(latestJobId);
      }
    }

    return currentJob;
  }, [currentJob, stepUuid, jobUuid, currentStep, jobs, stepsMap]);

  const contextValue = useMemo(
    () => ({
      currentJob: currentJob || null,
      currentJobUuid: jobUuid || currentJob?.id || null,
      currentLatestJob: currentLatestJob || null,
      currentLatestJobUuid: currentLatestJob?.id || null,
      currentStep: currentStep || null,
      currentStepUuid: stepUuid || currentStep?.uuid || null,
    }),
    [currentJob, jobUuid, currentLatestJob, currentStep, stepUuid],
  );

  return (
    <CurrentStepSelectionContext.Provider value={contextValue}>
      {props.children}
    </CurrentStepSelectionContext.Provider>
  );
}

/**
 * Hook for getting the current step or job from the URL based on either the step or job UUID.
 */
export default function useCurrentJobOrStep() {
  const context = useContext(CurrentStepSelectionContext);
  if (!context) {
    throw new Error(
      "useCurrentJobOrStep must be called in the context of a CurrentStepSelectionContextProvider",
    );
  }

  return context;
}

function findLatestRetry(job: CommandJob, jobs: Map<string, Job>) {
  if (job.retriedInJobUuid) {
    const retry = jobs.get(job.retriedInJobUuid);
    if (retry) {
      return findLatestRetry(retry as CommandJob, jobs);
    }
  }
  return job;
}
